import { reactive } from 'vue';
import type { MapAreaState, MapAreaConfig } from '@/modules/map-editor';

/**
 * Service for managing map area state.
 * Handles coordinates, dimensions, and update flags that drive the animation loop.
 */
export class MapAreaService {
  private static instance: MapAreaService | null = null;

  private readonly state: MapAreaState;
  private _wakeCallback: (() => void) | null = null;

  constructor(config: MapAreaConfig = {}) {
    this.state = reactive({
      x: config.initialX || 0,
      y: config.initialY || 0,
      width: config.width || 0,
      height: config.height || 0,
      borderColor: config.borderColor || '#0125ea',
      bgColor: config.bgColor || null,
      movement: false,
      update: false, // animation, constant rerendering until value is set to false
      updateElement: false, // single rerender
      nodesToUpdate: new Set<number>(),
      edgesToUpdate: new Set<number>(),
      mediaOnlyNodes: new Set<number>(),
    });
  }

  /**
   * Get singleton instance. If config is provided and instance already exists,
   * the config is merged into the existing state.
   */
  static getInstance(config?: MapAreaConfig): MapAreaService {
    if (!MapAreaService.instance) {
      MapAreaService.instance = new MapAreaService(config || {});
    } else if (config) {
      MapAreaService.instance.applyConfig(config);
    }
    return MapAreaService.instance;
  }

  applyConfig(config: MapAreaConfig): void {
    if (config.initialX !== undefined) this.state.x = config.initialX;
    if (config.initialY !== undefined) this.state.y = config.initialY;
    if (config.width !== undefined) this.state.width = config.width;
    if (config.height !== undefined) this.state.height = config.height;
    if (config.borderColor !== undefined) this.state.borderColor = config.borderColor;
    if (config.bgColor !== undefined) this.state.bgColor = config.bgColor;
  }

  static resetInstance(): void {
    MapAreaService.instance = null;
  }

  /**
   * Register a callback to wake the animation loop when dirty flags are set.
   */
  setWakeCallback(callback: (() => void) | null): void {
    this._wakeCallback = callback;
  }

  private wake(): void {
    if (this._wakeCallback) this._wakeCallback();
  }

  /**
   * Reset coordinates to origin without destroying the singleton.
   * Call when leaving the Map Builder so the next map load starts from (0, 0)
   * and the centering animation is visible.
   */
  resetCoordinates(): void {
    this.state.x = 0;
    this.state.y = 0;
    this.state.update = false;
    this.state.updateElement = false;
    this.state.movement = false;
  }

  getState(): MapAreaState {
    return this.state;
  }

  get x(): number {
    return this.state.x;
  }

  get y(): number {
    return this.state.y;
  }

  get width(): number {
    return this.state.width;
  }

  get height(): number {
    return this.state.height;
  }

  get bgColor(): string | null {
    return this.state.bgColor;
  }

  get nodesToUpdate(): Set<number> {
    return this.state.nodesToUpdate;
  }

  get edgesToUpdate(): Set<number> {
    return this.state.edgesToUpdate;
  }

  get movement(): boolean {
    return this.state.movement;
  }

  set movement(value: boolean) {
    this.state.movement = value;
  }

  setCoordinates(x: number, y: number): void {
    this.state.x = x;
    this.state.y = y;
    this.state.update = true;
    this.wake();
  }

  setX(x: number): void {
    this.state.x = x;
    this.state.update = true;
    this.wake();
  }

  setY(y: number): void {
    this.state.y = y;
    this.state.update = true;
    this.wake();
  }

  setDimensions(width: number, height: number): void {
    this.state.width = width;
    this.state.height = height;
    this.state.update = true;
    this.wake();
  }

  setWidth(width: number): void {
    this.state.width = width;
    this.state.update = true;
    this.wake();
  }

  setHeight(height: number): void {
    this.state.height = height;
    this.state.update = true;
    this.wake();
  }

  setBgColor(bgColor: string): void {
    this.state.bgColor = bgColor;
    this.state.update = true;
    this.wake();
  }

  updateProperties(properties: Partial<MapAreaState>): void {
    Object.assign(this.state, properties);
    this.state.update = true;
    this.wake();
  }

  /**
   * Set the continuous-render flag.
   * While true the animation loop redraws every frame (used during zoom/pan animations).
   */
  updateMap(value: boolean = true): void {
    this.state.update = value;
    if (value) this.wake();
  }

  /**
   * Request a single-frame element update.
   * When `id` is provided the element is queued for cache rebuild before the next draw.
   */
  updateElement(value: boolean = true, id: number | null = null, type: string = 'node'): void {
    if (id != null && !isNaN(id)) {
      if (!type || type === 'node') {
        this.state.nodesToUpdate.add(id);
      }
      if (type === 'edge') {
        this.state.edgesToUpdate.add(id);
      }
    }

    this.state.updateElement = value;
    if (value) this.wake();
  }

  /**
   * Request a media-only cache update (skips bounds recomputation).
   * Used by animation renderers (GIF/Lottie) where only media content changed.
   */
  updateMediaOnly(nodeId: number): void {
    // If already queued for full update, skip — full update is a superset
    if (!this.state.nodesToUpdate.has(nodeId)) {
      this.state.mediaOnlyNodes.add(nodeId);
    }
    this.state.updateElement = true;
    this.wake();
  }

  clearUpdates(): void {
    this.state.nodesToUpdate.clear();
    this.state.edgesToUpdate.clear();
    this.state.mediaOnlyNodes.clear();
  }

  resetUpdateElementFlag(): void {
    this.state.updateElement = false;
  }

  /**
   * Shorthand used by legacy store modules — delegates to updateElement(true).
   */
  renderFrame(): void {
    this.updateElement(true);
  }
}
